import {
  Heading,
  SearchImage,
  ShowMore,
} from '@rabbit/elements/shared-components';
import ROUTE_NAME from '../../../utils/url-constants';
import { DTVendable } from '@rabbit/data/types';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useRef, useState, useEffect } from 'react';

export interface SliderProps {
  title: string;
  actionButtonText: string;
  key?: string;
  data?: DTVendable[];
  onClick: (value: string) => void;
  onItemSelect?: (id: string, data: any) => void;
  viewType?: 'grid' | 'list';
  hideShowMore?: boolean;
}

export function SearchSlider({
  title,
  actionButtonText,
  data,
  onClick,
  onItemSelect,
  viewType = 'grid',
  hideShowMore = false,
}: SliderProps) {
  const navigate = useNavigate();
  const sliderRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  // Check if scrolling is needed and update arrow visibility
  const checkScroll = () => {
    if (!sliderRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [data]);

  const scroll = (direction: 'left' | 'right') => {
    if (!sliderRef.current) return;

    const scrollAmount = 300;
    const newScrollLeft = direction === 'left'
      ? sliderRef.current.scrollLeft - scrollAmount
      : sliderRef.current.scrollLeft + scrollAmount;

    sliderRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });

    // Update arrows after scrolling
    setTimeout(checkScroll, 400);
  };

  return data ? (
    <div className="w-full max-w-screen-xl">
      <div className="mt-6 mb-4 flex justify-between items-center">
        <div className="flex items-center">
          <div className="w-1 h-6 bg-primary-600 rounded-full mr-2"></div>
          <Heading kind="h3" className="capitalize" children={title} />
        </div>
        {data.length > 5 && !hideShowMore ? (
          <button
            className="text-primary-700 hover:text-primary-800 font-medium text-base flex items-center transition-colors duration-200"
            onClick={() => onClick(title)}
          >
            {actionButtonText}
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </button>
        ) : null}
      </div>

      {/* Products Section with Navigation Arrows */}
      <div className="relative group">
        {/* Only show navigation arrows in grid view */}
        {viewType === 'grid' && showLeftArrow && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 z-10 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 group-hover:-ml-6"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-700" />
          </button>
        )}

        <div
          ref={sliderRef}
          className={`${viewType === 'grid' ? 'flex gap-4 overflow-x-auto' : 'flex flex-col gap-4'} pb-6 pt-2 scrollbar-hide ${viewType === 'grid' ? 'scroll-smooth' : ''}`}
          onScroll={viewType === 'grid' ? checkScroll : undefined}
        >
          {data.length > 0 ? (
            <>
              {viewType === 'grid' ? (
                // Grid view
                <>
                  {data.slice(0, 5).map((item: any, index: number) => (
                    <div
                      key={index}
                      className="font-nunito flex-none rounded-lg shadow-sm hover:shadow-lg transition-all duration-300"
                    >
                      <SearchImage
                        imageUrl={item.img[0]}
                        size="sm"
                        subtitle={item.brand}
                        title={item.title}
                        mpn={item.mpn}
                        onClick={
                          !onItemSelect
                            ? () =>
                                navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                            : () => onItemSelect(item.docid, item)
                        }
                      />
                    </div>
                  ))}
                  {data.length > 5 && !hideShowMore ? (
                    <div className="flex-none cursor-pointer rounded-lg">
                      <ShowMore
                        actionText="Show More"
                        onClick={() => onClick(title)}
                        category={title}
                      />
                    </div>
                  ) : null}
                </>
              ) : (
                // List view
                <>
                  {data.slice(0, 5).map((item: any, index: number) => (
                    <div
                      key={index}
                      className="font-nunito w-full"
                    >
                      <div className="flex flex-col sm:flex-row rounded-lg bg-white shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-200 overflow-hidden">
                        <div className="sm:w-32 md:w-40 lg:w-64 h-32 sm:h-auto">
                          <div
                            style={{ backgroundImage: `url(${item.img[0]})` }}
                            className="w-full h-full bg-contain bg-no-repeat bg-center cursor-pointer hover:opacity-90 transition-opacity duration-300"
                            onClick={
                              !onItemSelect
                                ? () => navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                                : () => onItemSelect(item.docid, item)
                            }
                          ></div>
                        </div>
                        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between">
                          <div>
                            <div className="flex flex-wrap items-center mb-2">
                              <span className="inline-block px-2 py-1 text-xs font-semibold text-primary-700 bg-primary-50 rounded-full">
                                {item.brand}
                              </span>
                            </div>
                            <h3 className="text-base sm:text-lg font-medium text-gray-900 hover:text-primary-700 transition-colors duration-200 mb-2 cursor-pointer line-clamp-2"
                              onClick={
                                !onItemSelect
                                  ? () => navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                                  : () => onItemSelect(item.docid, item)
                              }
                            >
                              {item.title}
                            </h3>
                            <p className="text-sm text-primary-700 font-medium flex items-center">
                              <span className="text-xs text-gray-500 mr-1">MPN:</span> {item.mpn}
                            </p>
                          </div>
                          <div className="mt-3 sm:mt-4 flex justify-center sm:justify-end">
                            <button
                              onClick={
                                !onItemSelect
                                  ? () => navigate(`${ROUTE_NAME.PRODUCTS_PREVIEW}/${item.docid}`)
                                  : () => onItemSelect(item.docid, item)
                              }
                              className="w-full sm:w-auto text-sm text-white bg-primary-600 hover:bg-primary-700 font-medium px-4 py-2 rounded-lg transition-colors duration-200 flex items-center justify-center"
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {data.length > 5 && !hideShowMore ? (
                    <div className="w-full cursor-pointer">
                      <button
                        onClick={() => onClick(title)}
                        className="w-full py-3 text-primary-700 hover:text-primary-800 font-medium text-sm sm:text-base flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200 shadow-sm"
                      >
                        {actionButtonText}
                        <ChevronRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <div className="w-full py-6 text-center text-gray-500 bg-gray-50 rounded-lg">
              <p className="text-lg">No products found in this category.</p>
            </div>
          )}
        </div>

        {viewType === 'grid' && showRightArrow && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 z-10 bg-white rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200 opacity-0 group-hover:opacity-100 group-hover:-mr-6"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-700" />
          </button>
        )}
      </div>
    </div>
  ) : null;
}

export default SearchSlider;
